import { Button, Icon } from "@chakra-ui/react";
import { RiCheckboxMultipleLine } from "react-icons/ri";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { Selection } from "src/hooks/useMultiselectState";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { FormIdListResult, SchoolFormKeyRecord } from "../types";
import { BulkVerificationsAsyncState } from "../verifications/BulkVerificationsAsync";
import { resolveBulkTargets } from "./bulkTargets";
import { trackFormAction } from "./trackFormAction";

interface BulkVerifyButtonAsyncProps {
  formTemplate: GQL.FormTemplateFragment;
  onFetchAllIds: () => Promise<FormIdListResult>;
  selection: Selection<SchoolFormKeyRecord, unknown>;
}

export const BulkVerifyButtonAsync: React.FC<BulkVerifyButtonAsyncProps> = ({
  formTemplate,
  onFetchAllIds,
  selection,
}) => {
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useAvelaToast();

  const handleBulkVerify = async () => {
    let cacheKey: uuid | null | undefined;

    if (!selection.isInclusive) {
      cacheKey = (await onFetchAllIds()).cacheKey;
    }

    const bulkTargets = resolveBulkTargets(
      selection,
      formTemplate.id,
      cacheKey
    );
    if (!bulkTargets) {
      toast({
        title:
          cacheKey === null
            ? "No forms in the current list to process."
            : "Please select at least one form to continue.",
        status: "info",
      });
      return;
    }

    trackFormAction({
      verb: "Clicked",
      actionType: "VERIFY_FORM",
      bulk: true,
      count: selection.size,
      async: true,
    });

    navigate(
      organization
        .map((org) =>
          Url.OrgAdmin.Forms.verifications({
            organization: org,
            formTemplateId: formTemplate.id,
            params: searchParams.toString(),
          })
        )
        .withDefault("#"),
      {
        state: {
          formTemplateId: formTemplate.id,
          bulkTargets,
          selectionSize: selection.size,
        } satisfies BulkVerificationsAsyncState,
      }
    );
  };

  return (
    <Button
      leftIcon={<Icon as={RiCheckboxMultipleLine} />}
      onClick={handleBulkVerify}
      size="sm"
      variant="banner"
    >
      Verify
    </Button>
  );
};
